import React, { useState, useEffect } from 'react';
import { X, MapPin, Building, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

export default function CommuneFormModal({ commune, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    district: '',
    description: '',
    population: '',
    average_price: '',
    amenities: {
      schools: [],
      hospitals: [],
      markets: [],
      transport: [],
      recreation: []
    }
  });

  const [amenityInput, setAmenityInput] = useState({
    schools: '',
    hospitals: '',
    markets: '',
    transport: '',
    recreation: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (commune) {
      setFormData({
        name: commune?.name || '',
        district: commune?.district || '',
        description: commune?.description || '',
        population: commune?.population ? commune?.population?.toString() : '',
        average_price: commune?.average_price ? commune?.average_price?.toString() : '',
        amenities: commune?.amenities || {
          schools: [],
          hospitals: [],
          markets: [],
          transport: [],
          recreation: []
        }
      });
    }
  }, [commune]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Le nom de la commune est requis';
    }
    
    if (!formData?.district?.trim()) {
      newErrors.district = 'Le district est requis';
    }
    
    if (formData?.population && isNaN(parseInt(formData?.population))) {
      newErrors.population = 'La population doit être un nombre';
    }
    
    if (formData?.average_price && isNaN(parseFloat(formData?.average_price))) {
      newErrors.average_price = 'Le prix moyen doit être un nombre';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const submitData = {
        name: formData?.name?.trim(),
        district: formData?.district?.trim(),
        description: formData?.description?.trim() || null,
        population: formData?.population ? parseInt(formData?.population) : null,
        average_price: formData?.average_price ? parseFloat(formData?.average_price) : null,
        amenities: formData?.amenities
      };
      
      await onSave(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const addAmenity = (category) => {
    const value = amenityInput?.[category]?.trim();
    if (value && !formData?.amenities?.[category]?.includes(value)) {
      setFormData(prev => ({
        ...prev,
        amenities: {
          ...prev?.amenities,
          [category]: [...(prev?.amenities?.[category] || []), value]
        }
      }));
      setAmenityInput(prev => ({ ...prev, [category]: '' }));
    }
  };

  const removeAmenity = (category, index) => {
    setFormData(prev => ({
      ...prev,
      amenities: {
        ...prev?.amenities,
        [category]: prev?.amenities?.[category]?.filter((_, i) => i !== index) || []
      }
    }));
  };

  const amenityCategories = [
    { key: 'schools', label: 'Écoles', icon: Building },
    { key: 'hospitals', label: 'Hôpitaux', icon: Building },
    { key: 'markets', label: 'Marchés', icon: Building },
    { key: 'transport', label: 'Transport', icon: Building },
    { key: 'recreation', label: 'Loisirs', icon: Building }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold flex items-center">
            <MapPin className="w-6 h-6 mr-2 text-blue-600" />
            {commune ? 'Modifier la commune' : 'Nouvelle commune'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 border-b pb-2">Informations de base</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la commune *
                </label>
                <Input
                  value={formData?.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="Nom de la commune"
                  className={errors?.name ? 'border-red-300' : ''}
                />
                {errors?.name && (
                  <p className="text-sm text-red-600 mt-1">{errors?.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District *
                </label>
                <Input
                  value={formData?.district}
                  onChange={(e) => setFormData(prev => ({ ...prev, district: e?.target?.value }))}
                  placeholder="District"
                  className={errors?.district ? 'border-red-300' : ''}
                />
                {errors?.district && (
                  <p className="text-sm text-red-600 mt-1">{errors?.district}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Population
                </label>
                <Input
                  type="number"
                  value={formData?.population}
                  onChange={(e) => setFormData(prev => ({ ...prev, population: e?.target?.value }))}
                  placeholder="Population"
                  className={errors?.population ? 'border-red-300' : ''}
                />
                {errors?.population && (
                  <p className="text-sm text-red-600 mt-1">{errors?.population}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prix moyen (XOF)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData?.average_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, average_price: e?.target?.value }))}
                  placeholder="Prix moyen"
                  className={errors?.average_price ? 'border-red-300' : ''}
                />
                {errors?.average_price && (
                  <p className="text-sm text-red-600 mt-1">{errors?.average_price}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Description de la commune"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Amenities */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 border-b pb-2">Commodités</h3>
              
              {amenityCategories?.map(({ key, label, icon: Icon }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {label}
                  </label>
                  
                  {/* Add amenity */}
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={amenityInput?.[key] || ''}
                      onChange={(e) => setAmenityInput(prev => ({ ...prev, [key]: e?.target?.value }))}
                      placeholder={`Ajouter ${label?.toLowerCase()}`}
                      className="flex-1"
                      onKeyPress={(e) => {
                        if (e?.key === 'Enter') {
                          e?.preventDefault();
                          addAmenity(key);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => addAmenity(key)}
                      className="px-3 py-2"
                    >
                      +
                    </Button>
                  </div>
                  
                  {/* Display amenities */}
                  {formData?.amenities?.[key] && formData?.amenities?.[key]?.length > 0 && (
                    <div className="space-y-1">
                      {formData?.amenities?.[key]?.map((amenity, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
                          <span>{amenity}</span>
                          <button
                            type="button"
                            onClick={() => removeAmenity(key, index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {commune ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}